"use client";

import { analytics } from "./client";

/**
 * Common event tracking functions for critical user actions
 */

// User Authentication Events
export const trackUserSignedOut = () => {
	analytics?.capture("user_signed_out");
};

// Onboarding Events
export const trackOnboardingStarted = () => {
	analytics?.capture("onboarding_started");
};

export const trackOnboardingCompleted = (data?: {
	brandName?: string;
	promptsCount?: number;
}) => {
	analytics?.capture("onboarding_completed", data);
};

// Brand Events
export const trackBrandUpdated = (brandName: string) => {
	analytics?.capture("brand_updated", { brand_name: brandName });
};

export const trackBrandSettingsOpened = () => {
	analytics?.capture("brand_settings_opened");
};

// Profile Events
export const trackProfileOpened = () => {
	analytics?.capture("profile_opened");
};

// Navigation Events
export const trackPageViewed = (
	pageName: string,
	properties?: Record<string, string | number>,
) => {
	analytics?.capture("page_viewed", { page_name: pageName, ...properties });
};

export const trackSidebarNavigation = (page: string, fromPage?: string) => {
	analytics?.capture("sidebar_navigation", {
		to_page: page,
		from_page: fromPage,
	});
};

// Analytics Table Events
export const trackAnalyticsTableViewed = (
	tableType: string,
	filters?: Record<string, string | number>,
) => {
	analytics?.capture("analytics_table_viewed", {
		table_type: tableType,
		...filters,
	});
};

// Insight Events
export const trackInsightCreated = (
	insightType: string,
	config?: Record<string, string | number>,
) => {
	analytics?.capture("insight_created", {
		insight_type: insightType,
		...config,
	});
};

export const trackInsightDeleted = (
	insightType: string,
	insightId?: string,
) => {
	analytics?.capture("insight_deleted", {
		insight_type: insightType,
		insight_id: insightId,
	});
};

export const trackFeatureUsed = (
	featureName: string,
	properties?: Record<string, string | number>,
) => {
	analytics?.capture("feature_used", {
		feature_name: featureName,
		...properties,
	});
};

// Error Events
export const trackError = (
	errorMessage: string,
	errorType?: string,
	errorStack?: string,
) => {
	analytics?.capture("error_occurred", {
		error_message: errorMessage,
		error_type: errorType,
		error_stack: errorStack,
	});
};

export const trackFilterApplied = (
	filterType: string,
	filterValue: string | string[],
	context: string,
) => {
	analytics?.capture("filter_applied", {
		filter_type: filterType,
		filter_value: Array.isArray(filterValue)
			? filterValue.join(",")
			: filterValue,
		context: context,
	});
};

// Prompts Events (CORE FEATURE - Most Important!)
export const trackPromptCreated = (
	promptType: "visibility" | "sentiment",
	topic?: string,
	region?: string,
) => {
	analytics?.capture("prompt_created", {
		prompt_type: promptType,
		topic: topic,
		region: region,
	});
};

export const trackPromptEdited = (
	promptId: string,
	promptType: "visibility" | "sentiment",
) => {
	analytics?.capture("prompt_edited", {
		prompt_id: promptId,
		prompt_type: promptType,
	});
};

export const trackPromptDeleted = (
	promptId: string,
	promptType: "visibility" | "sentiment",
) => {
	analytics?.capture("prompt_deleted", {
		prompt_id: promptId,
		prompt_type: promptType,
	});
};

export const trackPromptsExported = (
	exportFormat: "csv" | "json",
	groupBy: string,
	count?: number,
) => {
	analytics?.capture("prompts_exported", {
		export_format: exportFormat,
		group_by: groupBy,
		prompts_count: count,
	});
};

// AI Insights Events (CRITICAL - Revenue Driver!)
export const trackAiAnalysisStarted = (analysisType: string) => {
	analytics?.capture("ai_analysis_started", {
		analysis_type: analysisType,
	});
};

export const trackAiAnalysisCompleted = (
	analysisType: string,
	opportunitiesFound?: number,
) => {
	analytics?.capture("ai_analysis_completed", {
		analysis_type: analysisType,
		opportunities_found: opportunitiesFound,
	});
};

export const trackOpportunityViewed = (
	opportunityId: string,
	category?: string,
	priority?: string,
) => {
	analytics?.capture("opportunity_viewed", {
		opportunity_id: opportunityId,
		category: category,
		priority: priority,
	});
};

export const trackContentGenerationStarted = (
	fromOpportunity: boolean,
	opportunityId?: string,
) => {
	analytics?.capture("content_generation_started", {
		from_opportunity: fromOpportunity,
		opportunity_id: opportunityId,
	});
};

export const trackContentCopied = (contentId: string) => {
	analytics?.capture("content_copied", {
		content_id: contentId,
	});
};

// Data Export Event
export const trackDocumentExported = (
	documentType: string,
	exportFormat: string,
	context?: string,
) => {
	analytics?.capture("document_exported", {
		document_type: documentType,
		export_format: exportFormat,
		context: context,
	});
};
